// useIsVisible is a custom hook that allows you to detect when an element is
// visible on the screen. It takes an object as an argument, and returns an object
// with two properties: setRef and inView.

import { useCallback, useEffect, useRef, useState } from 'react'

export function useIsVisible({
  root = null,
  rootMargin = '0px',
  threshold = 1.0,
  once = false,
}: {
  root?: Element | Document | null
  rootMargin?: string
  threshold?: number
  once?: boolean
}) {
  const observer = useRef<any>(null)
  const ref = useRef<any>(null)
  const [inView, setInView] = useState(false)

  const setRef = useCallback((node) => {
    if (!ref.current) {
      ref.current = node
    }
  }, [])

  const callbackFunction = useCallback((entries) => {
    const [entry] = entries
    setInView(entry.isIntersecting)
  }, [])

  useEffect(() => {
    if (ref.current) {
      observer.current = new IntersectionObserver(callbackFunction, {
        root,
        rootMargin,
        threshold,
      })
      observer.current.observe(ref.current)
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [callbackFunction])

  useEffect(() => {
    if (once && inView) {
      observer.current.disconnect()
    }
  }, [inView])

  return { setRef, inView }
}
